/**
 * Investment Manager — the "what to do today" cockpit (M3).
 *
 * Three stacked sections:
 *   1. Coverage health — is the scoring engine producing signal?
 *   2. What to do today — per-holding ACCUMULATE/HOLD/REDUCE/EXIT (M2).
 *   3. Build a portfolio — risk-profiled recommender (M1).
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  getPositionActions,
  getScoringCoverage,
  listPortfoliosApi,
  recommendPortfolio,
  scanActionAlerts,
  type PositionAction,
} from "@/lib/api";
import { Panel } from "@/components/ui/Panel";
import { Stat } from "@/components/ui/Stat";
import { DataTable } from "@/components/ui/Table";
import { Pill } from "@/components/ui/Pill";
import { Button } from "@/components/ui/Button";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/States";
import { fmtDate, fmtPercent } from "@/lib/format";

type Risk = "conservative" | "balanced" | "aggressive";

const ACTION_TONE: Record<PositionAction["action"], "good" | "ok" | "warn" | "bad" | "muted"> = {
  ACCUMULATE: "good",
  HOLD: "muted",
  REDUCE: "warn",
  EXIT: "bad",
  REVIEW: "ok",
};

const BAND_TONE: Record<string, "good" | "ok" | "warn" | "bad" | "muted"> = {
  high: "good",
  medium: "ok",
  low: "warn",
  insufficient: "bad",
};

const usd = (n: number) => `$${Math.round(n).toLocaleString()}`;

export function ManagerPage() {
  return (
    <div className="space-y-4">
      <header className="space-y-0.5">
        <h1 className="text-xl font-semibold tracking-tight">Investment manager</h1>
        <p className="text-2xs text-foreground-muted">
          Your standing orders for today, and a recommender to build a new book. Path A —
          it recommends; you execute and record fills under{" "}
          <Link to="/portfolio" className="text-accent-strong hover:underline">
            Portfolio
          </Link>
          .
        </p>
      </header>

      <CoverageStrip />
      <TodaySection />
      <RecommendSection />
    </div>
  );
}

// ---------------------------------------------------------------------------
// 1. Coverage health
// ---------------------------------------------------------------------------

function CoverageStrip() {
  const q = useQuery({
    queryKey: ["scoring", "coverage"],
    queryFn: getScoringCoverage,
    staleTime: 5 * 60_000,
  });

  if (q.isLoading) return <LoadingState message="Checking engine health…" />;
  if (q.isError || !q.data) return null;

  const rows = q.data;
  const equity = rows.find((r) => r.weight_vector_id === 1);
  const totalScored = rows.reduce((a, r) => a + r.scored, 0);
  const asOf = rows.find((r) => r.latest_as_of)?.latest_as_of ?? null;

  return (
    <div className="grid gap-3 sm:grid-cols-4">
      <Panel>
        <Stat
          label="Equity coverage"
          value={equity ? `${equity.coverage_pct}%` : "—"}
          hint={equity ? `${equity.scored} / ${equity.universe_size} scored` : undefined}
          tone={equity && equity.coverage_pct > 60 ? "good" : "warn"}
        />
      </Panel>
      <Panel>
        <Stat label="Total scored rows" value={totalScored.toLocaleString()} hint="across all lenses" />
      </Panel>
      <Panel>
        <Stat
          label="Vectors live"
          value={`${rows.filter((r) => r.scored > 0).length} / ${rows.length}`}
          hint="weight vectors producing scores"
        />
      </Panel>
      <Panel>
        <Stat label="As of" value={asOf ? fmtDate(asOf) : "—"} hint="latest scoring run" />
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 2. What to do today (M2)
// ---------------------------------------------------------------------------

function TodaySection() {
  const portfolios = useQuery({ queryKey: ["portfolios"], queryFn: listPortfoliosApi });
  const [portfolioId, setPortfolioId] = useState<number | null>(null);
  const [wv, setWv] = useState(1);

  const selected = portfolioId ?? portfolios.data?.[0]?.id ?? null;

  const actions = useQuery({
    queryKey: ["portfolio", selected, "actions", wv],
    queryFn: () => getPositionActions(selected as number, wv),
    enabled: selected !== null,
    staleTime: 60_000,
  });

  // M4: preview which positions would fire an alert right now (dry-run; does
  // NOT POST to the webhook). One click, idempotent — the result vanishes
  // when the user changes portfolio/lens.
  const scan = useMutation({
    mutationFn: () =>
      scanActionAlerts(selected as number, { weightVectorId: wv, deliverWebhook: false }),
  });

  return (
    <Panel
      title="What to do today"
      description="Per-position standing orders from the active policy, most urgent first."
      action={
        <div className="flex items-center gap-2">
          {selected !== null && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => scan.mutate()}
              loading={scan.isPending}
              title="Dry-run: which positions would fire a webhook alert?"
            >
              Preview alerts
            </Button>
          )}
          {portfolios.data && portfolios.data.length > 0 && (
            <select
              value={selected ?? ""}
              onChange={(e) => {
                setPortfolioId(Number(e.target.value));
                scan.reset();
              }}
              className="rounded-md border border-border bg-surface-base px-2 py-1 text-xs"
            >
              {portfolios.data.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <select
            value={wv}
            onChange={(e) => {
              setWv(Number(e.target.value));
              scan.reset();
            }}
            className="rounded-md border border-border bg-surface-base px-2 py-1 text-xs"
            title="Scoring lens"
          >
            <option value={1}>Quality</option>
            <option value={2}>Value</option>
            <option value={3}>GARP</option>
            <option value={4}>Momentum</option>
          </select>
        </div>
      }
    >
      {scan.data && (
        <div className="mb-3 rounded-md border border-border-subtle bg-surface-base px-3 py-2 text-xs text-foreground-secondary">
          {scan.data.alerts_fired === 0
            ? `Dry-run: 0 of ${scan.data.holdings_checked} positions would fire — no action changes since the last alert.`
            : `Dry-run: ${scan.data.alerts_fired} of ${scan.data.holdings_checked} positions would fire — ` +
              scan.data.outcomes
                .filter((o) => o.fired)
                .map((o) => `${o.ticker} ${o.prior_action ?? "—"}→${o.action}`)
                .join(", ")}
        </div>
      )}
      {portfolios.isLoading && <LoadingState />}
      {portfolios.data && portfolios.data.length === 0 && (
        <EmptyState
          title="No portfolios yet"
          description="Build one with the recommender below, then record what you bought to see daily actions here."
        />
      )}
      {selected !== null && actions.isLoading && <LoadingState message="Reading positions…" />}
      {actions.isError && <ErrorState detail="Could not load position actions." />}
      {actions.data && (
        <div className="space-y-3">
          <ActionSummary summary={actions.data.summary} asOf={actions.data.as_of} />
          {actions.data.positions.length === 0 ? (
            <EmptyState title="No holdings" description="This portfolio has no positions." />
          ) : (
            <DataTable
              dense
              rows={actions.data.positions}
              rowKey={(p) => p.security_id}
              columns={[
                {
                  key: "action",
                  header: "Action",
                  cell: (p) => <Pill tone={ACTION_TONE[p.action]}>{p.action}</Pill>,
                },
                {
                  key: "ticker",
                  header: "Ticker",
                  cell: (p) => (
                    <Link
                      to={`/company/${p.security_id}`}
                      className="font-medium text-foreground-primary hover:text-accent-strong"
                    >
                      {p.ticker}
                    </Link>
                  ),
                },
                { key: "composite", header: "Score", align: "right", cell: (p) => p.composite.toFixed(0) },
                {
                  key: "conf",
                  header: "Conf.",
                  cell: (p) => <Pill tone={BAND_TONE[p.confidence_band] ?? "muted"}>{p.confidence_band}</Pill>,
                },
                {
                  key: "weight",
                  header: "Weight",
                  align: "right",
                  cell: (p) => (p.current_weight !== null ? fmtPercent(p.current_weight) : "—"),
                },
                { key: "reason", header: "Why", cell: (p) => <span className="text-foreground-secondary">{p.reason}</span> },
              ]}
            />
          )}
        </div>
      )}
    </Panel>
  );
}

function ActionSummary({ summary, asOf }: { summary: Record<string, number>; asOf: string | null }) {
  const order: PositionAction["action"][] = ["EXIT", "REDUCE", "ACCUMULATE", "HOLD", "REVIEW"];
  return (
    <div className="flex flex-wrap items-center gap-2">
      {order.map((a) =>
        summary[a] ? (
          <Pill key={a} tone={ACTION_TONE[a]}>
            {a} · {summary[a]}
          </Pill>
        ) : null,
      )}
      {asOf && <span className="text-2xs text-foreground-muted">scores as of {fmtDate(asOf)}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3. Build a portfolio (M1)
// ---------------------------------------------------------------------------

const RISKS: { key: Risk; label: string; blurb: string }[] = [
  { key: "conservative", label: "Conservative", blurb: "min-variance · quality" },
  { key: "balanced", label: "Balanced", blurb: "risk-parity · GARP" },
  { key: "aggressive", label: "Aggressive", blurb: "max-Sharpe · momentum" },
];

function RecommendSection() {
  const [risk, setRisk] = useState<Risk>("balanced");
  const [capital, setCapital] = useState(100000);

  const rec = useMutation({
    mutationFn: () => recommendPortfolio({ risk_appetite: risk, capital }),
  });

  const data = rec.data;
  const cashPct = useMemo(
    () => (data && data.capital > 0 ? data.cash_usd / data.capital : 0),
    [data],
  );

  return (
    <Panel
      title="Build a portfolio"
      description="Risk appetite + capital → a $-allocated model book with rationale."
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex gap-1.5">
          {RISKS.map((r) => (
            <button
              key={r.key}
              onClick={() => setRisk(r.key)}
              className={`rounded-md border px-3 py-2 text-left text-xs transition ${
                risk === r.key
                  ? "border-accent bg-accent-subtle text-accent-strong"
                  : "border-border bg-surface-base text-foreground-secondary hover:bg-surface-subtle"
              }`}
            >
              <div className="font-medium">{r.label}</div>
              <div className="text-2xs text-foreground-muted">{r.blurb}</div>
            </button>
          ))}
        </div>
        <label className="space-y-1 text-xs text-foreground-secondary">
          <span className="block text-2xs uppercase tracking-wider text-foreground-muted">Capital</span>
          <input
            type="number"
            min={1000}
            step={1000}
            value={capital}
            onChange={(e) => setCapital(Math.max(0, Number(e.target.value)))}
            className="w-32 rounded-md border border-border bg-surface-base px-2 py-1.5 text-sm tabular-nums"
          />
        </label>
        <Button variant="primary" loading={rec.isPending} onClick={() => rec.mutate()}>
          Recommend
        </Button>
      </div>

      {rec.isError && <div className="pt-3"><ErrorState detail="Recommendation failed — try a different lens or capital." /></div>}

      {data && (
        <div className="space-y-3 pt-4">
          <div className="grid gap-3 sm:grid-cols-4">
            <Panel>
              <Stat label="Deployed" value={usd(data.deployed_usd)} hint={`${fmtPercent(data.gross_exposure)} gross`} />
            </Panel>
            <Panel>
              <Stat label="Cash buffer" value={usd(data.cash_usd)} hint={fmtPercent(cashPct)} tone={cashPct > 0.5 ? "warn" : "default"} />
            </Panel>
            <Panel>
              <Stat label="Exp. vol" value={data.expected_vol !== null ? fmtPercent(data.expected_vol) : "—"} hint={`mode: ${data.optimizer_mode}`} />
            </Panel>
            <Panel>
              <Stat label="Positions" value={data.positions.length} hint={`lens wv${data.weight_vector_id}`} />
            </Panel>
          </div>

          {data.rationale.length > 0 && (
            <ul className="space-y-1 rounded-md border border-border-subtle bg-surface-base px-4 py-3 text-xs text-foreground-secondary">
              {data.rationale.map((line, i) => (
                <li key={i}>• {line}</li>
              ))}
            </ul>
          )}

          <DataTable
            dense
            rows={data.positions}
            rowKey={(p) => p.security_id}
            columns={[
              {
                key: "ticker",
                header: "Ticker",
                cell: (p) => (
                  <Link to={`/company/${p.security_id}`} className="font-medium text-foreground-primary hover:text-accent-strong">
                    {p.ticker}
                  </Link>
                ),
              },
              { key: "alloc", header: "Allocation", align: "right", cell: (p) => usd(p.allocation_usd) },
              { key: "weight", header: "Weight", align: "right", cell: (p) => fmtPercent(p.weight) },
              { key: "composite", header: "Score", align: "right", cell: (p) => p.composite.toFixed(0) },
              { key: "conf", header: "Conf.", cell: (p) => <Pill tone={BAND_TONE[p.confidence_band] ?? "muted"}>{p.confidence_band}</Pill> },
            ]}
          />
        </div>
      )}
    </Panel>
  );
}
